import { useEffect, useMemo, useState } from 'react';
import { bugtracker } from '@99designs/design-services-common';
import { PreviousDesigner } from './DesignerSelector';
import { ANY_DESIGNER_CALENDAR_ID } from './appointmentValue';
import { useDesignLivePreviousDesignersQuery } from './appointments.generated';

interface UseDesignLiveDesignerSelectionParams {
    isSignedIn: boolean;
    // Designer-matching feature gate.
    enabled: boolean;
    initialCalendarId: number;
    // Saved-name fallback while loading.
    initialDesignerName: string | undefined;
    // Saved explicit toggle choice.
    initialMatchWithPreviousDesigner: boolean | undefined;
    // Prevents reassignment of legacy appointments.
    hasExistingAppointment: boolean;
    // Clears a slot invalidated by selection changes.
    onSelectionCleared: () => void;
}

interface UseDesignLiveDesignerSelectionResult {
    previousDesigners: PreviousDesigner[];
    isLoadingDesigners: boolean;
    // Distinguishes errors from empty results.
    hasDesignersError: boolean;
    matchWithPreviousDesigner: boolean;
    // Undefined until explicitly chosen.
    matchWithPreviousDesignerChoice: boolean | undefined;
    calendarId: number;
    // Undefined means "any designer."
    effectiveCalendarId: number | undefined;
    selectedDesignerName: string | undefined;
    onToggleChange: (activated: boolean) => void;
    onCalendarChange: (calendarId: number) => void;
}

// Falls back to the first designer, then "any."
function resolveSpecificCalendarId(
    calendarId: number,
    previousDesigners: PreviousDesigner[]
): number {
    if (previousDesigners.some((d) => d.acuityCalendarId === calendarId)) {
        return calendarId;
    }
    return previousDesigners[0]?.acuityCalendarId ?? ANY_DESIGNER_CALENDAR_ID;
}

// Trusts the saved calendar until designers load.
function resolveSelectedCalendarId(
    matching: boolean,
    calendarId: number,
    previousDesigners: PreviousDesigner[],
    designersResolved: boolean
): number {
    if (!matching) {
        return ANY_DESIGNER_CALENDAR_ID;
    }
    if (!designersResolved) {
        return calendarId;
    }
    return resolveSpecificCalendarId(calendarId, previousDesigners);
}

// Owns designer lookup, matching, and calendar selection.
export function useDesignLiveDesignerSelection({
    isSignedIn,
    enabled,
    initialCalendarId,
    initialDesignerName,
    initialMatchWithPreviousDesigner,
    hasExistingAppointment,
    onSelectionCleared,
}: UseDesignLiveDesignerSelectionParams): UseDesignLiveDesignerSelectionResult {
    const [calendarId, setCalendarId] = useState<number>(initialCalendarId);
    // null follows the sign-in default.
    const [matchToggle, setMatchToggle] = useState<boolean | null>(
        initialMatchWithPreviousDesigner ??
            (initialCalendarId !== ANY_DESIGNER_CALENDAR_ID ? true : null)
    );

    // Only signed-in customers need this lookup.
    const {
        data: designersData,
        loading: isLoadingDesigners,
        error: designersError,
    } = useDesignLivePreviousDesignersQuery({
        skip: !enabled || !isSignedIn,
        onError: (e) => {
            bugtracker.notify(`Failed to load previous DesignLive designers: ${e.message}`);
        },
    });
    const previousDesigners = useMemo(
        () => designersData?.designLivePreviousDesigners ?? [],
        [designersData]
    );
    const hasPreviousDesigners = previousDesigners.length > 0;

    // Legacy appointments remain assigned to "any."
    const isPreExistingAnyDesignerAppointment =
        hasExistingAppointment &&
        initialCalendarId === ANY_DESIGNER_CALENDAR_ID &&
        initialMatchWithPreviousDesigner === undefined;

    const defaultMatchWithPreviousDesigner = isSignedIn && !isPreExistingAnyDesignerAppointment;

    const matchWithPreviousDesigner = enabled
        ? matchToggle ?? defaultMatchWithPreviousDesigner
        : false;

    // Persist only explicit choices.
    const matchWithPreviousDesignerChoice = enabled ? matchToggle ?? undefined : undefined;

    // Keep selection consistent before reconciliation runs.
    const selectedCalendarId = resolveSelectedCalendarId(
        matchWithPreviousDesigner,
        calendarId,
        previousDesigners,
        Boolean(designersData)
    );
    const effectiveCalendarId =
        selectedCalendarId !== ANY_DESIGNER_CALENDAR_ID ? selectedCalendarId : undefined;

    const selectedDesigner = previousDesigners.find(
        (d) => d.acuityCalendarId === selectedCalendarId
    );
    // Keep the saved name while loading.
    const resolvedDesignerName =
        selectedDesigner?.designerName ??
        (selectedCalendarId === initialCalendarId ? initialDesignerName : undefined);
    const selectedDesignerName = matchWithPreviousDesigner ? resolvedDesignerName : undefined;

    const onCalendarChange = (newCalendarId: number) => {
        // Designer changes invalidate the slot.
        setCalendarId(newCalendarId);
        onSelectionCleared();
    };

    const onToggleChange = (activated: boolean) => {
        // Clear only when the booked calendar changes.
        const nextSpecificCalendarId = resolveSpecificCalendarId(calendarId, previousDesigners);
        const nextEffectiveCalendarId =
            activated && nextSpecificCalendarId !== ANY_DESIGNER_CALENDAR_ID
                ? nextSpecificCalendarId
                : undefined;
        if (nextEffectiveCalendarId !== effectiveCalendarId) {
            onSelectionCleared();
        }
        setMatchToggle(activated);
    };

    // Reconcile the stored calendar after loading.
    useEffect(() => {
        if (!designersData) {
            return;
        }
        const reconciledCalendarId = resolveSelectedCalendarId(
            matchWithPreviousDesigner,
            calendarId,
            previousDesigners,
            true
        );
        if (reconciledCalendarId === calendarId) {
            return;
        }
        // Replacing a stale designer invalidates the slot.
        if (
            matchWithPreviousDesigner &&
            hasPreviousDesigners &&
            calendarId !== ANY_DESIGNER_CALENDAR_ID
        ) {
            onSelectionCleared();
        }
        setCalendarId(reconciledCalendarId);
    }, [
        designersData,
        matchWithPreviousDesigner,
        hasPreviousDesigners,
        previousDesigners,
        calendarId,
        onSelectionCleared,
    ]);

    return {
        previousDesigners,
        isLoadingDesigners,
        hasDesignersError: Boolean(designersError),
        matchWithPreviousDesigner,
        matchWithPreviousDesignerChoice,
        calendarId: selectedCalendarId,
        effectiveCalendarId,
        selectedDesignerName,
        onToggleChange,
        onCalendarChange,
    };
}
